import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import modelAPI from 'app/apiModel';
import { setSnackbar } from './snackbarSlice';

export const fetchProjectModels = createAsyncThunk(
    'projectModels/fetchAll',
    async ({ projectId }, { dispatch, rejectWithValue }) => {
        try {
            const res = await modelAPI.get(`/v0.4/projects/${projectId}/models`);
            return res.data;
        } catch (err) {
            dispatch(setSnackbar({ status: 'error', message: 'Unable to load models' }));
            return rejectWithValue(err.response?.data?.message || 'Failed to load models');
        }
    }
);

export const fetchModelFiles = createAsyncThunk(
    'projectModels/fetchFiles',
    async ({ projectId, modelId, version }, { dispatch, rejectWithValue }) => {
        try {
            const params = version != null ? { version } : {};
            const res = await modelAPI.get(
                `/v0.4/projects/${projectId}/models/${modelId}/files`,
                { params }
            );
            return { modelId, version: res.data.version, files: res.data.files ?? [] };
        } catch (err) {
            dispatch(setSnackbar({ status: 'error', message: 'Unable to load model files' }));
            return rejectWithValue(err.response?.data?.message || 'Failed to load model files');
        }
    }
);

export const downloadModelFile = createAsyncThunk(
    'projectModels/downloadFile',
    async ({ projectId, modelId, fileName, version }, { dispatch, rejectWithValue }) => {
        try {
            // Ask for the presigned URL as JSON rather than following the API's 302: the
            // redirect lands on S3 cross-origin, and the bucket sends no CORS headers, so
            // an XHR download is blocked. Navigating an anchor to the URL is not subject
            // to CORS. The presigned URL carries Content-Disposition with the file name,
            // so no `download` attribute is needed (browsers ignore it cross-origin anyway).
            const params = version != null ? { version, redirect: false } : { redirect: false };
            const { data } = await modelAPI.get(
                `/v0.4/projects/${projectId}/models/${modelId}/files/${encodeURIComponent(fileName)}`,
                { params }
            );
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = data.url;
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            return data.fileName ?? fileName;
        } catch (err) {
            dispatch(setSnackbar({ status: 'error', message: 'Unable to download file' }));
            return rejectWithValue(err.response?.data?.message || 'Failed to download file');
        }
    }
);

export const updateProjectModel = createAsyncThunk(
    'projectModels/update',
    async ({ projectId, modelId, modelName, description, transformJson }, { dispatch, rejectWithValue }) => {
        try {
            const body = {};
            if (modelName !== undefined) body.modelName = modelName;
            if (description !== undefined) body.description = description;
            if (transformJson !== undefined) body.transformJson = transformJson;
            const res = await modelAPI.put(
                `/v0.4/projects/${projectId}/models/${modelId}`,
                body
            );
            dispatch(setSnackbar({ status: 'success', message: 'Model updated' }));
            dispatch(fetchProjectModels({ projectId }));
            return res.data;
        } catch (err) {
            dispatch(setSnackbar({ status: 'error', message: 'Failed to update model' }));
            return rejectWithValue(err.response?.data?.message || 'Failed to update model');
        }
    }
);

export const deleteProjectModel = createAsyncThunk(
    'projectModels/delete',
    async ({ projectId, modelId }, { dispatch, rejectWithValue }) => {
        try {
            await modelAPI.delete(`/v0.4/projects/${projectId}/models/${modelId}`);
            dispatch(setSnackbar({ status: 'success', message: 'Model deleted' }));
            dispatch(fetchProjectModels({ projectId }));
            return modelId;
        } catch (err) {
            dispatch(setSnackbar({ status: 'error', message: 'Failed to delete model' }));
            return rejectWithValue(err.response?.data?.message || 'Failed to delete model');
        }
    }
);

export const updateModelVersion = createAsyncThunk(
    'projectModels/updateVersion',
    async ({ projectId, modelId, versionId, notes }, { dispatch, rejectWithValue }) => {
        try {
            const res = await modelAPI.patch(
                `/v0.4/projects/${projectId}/models/${modelId}/versions/${versionId}`,
                { notes }
            );
            dispatch(setSnackbar({ status: 'success', message: 'Version updated' }));
            dispatch(fetchProjectModels({ projectId }));
            return { modelId, ...res.data };
        } catch (err) {
            dispatch(setSnackbar({ status: 'error', message: 'Failed to update version' }));
            return rejectWithValue(err.response?.data?.message || 'Failed to update version');
        }
    }
);

export const setCurrentModelVersion = createAsyncThunk(
    'projectModels/setCurrentVersion',
    async ({ projectId, modelId, versionId }, { dispatch, rejectWithValue }) => {
        try {
            await modelAPI.post(
                `/v0.4/projects/${projectId}/models/${modelId}/versions/${versionId}/set-current`
            );
            dispatch(setSnackbar({ status: 'success', message: 'Current version updated' }));
            dispatch(fetchProjectModels({ projectId }));
            return { modelId, versionId };
        } catch (err) {
            dispatch(setSnackbar({ status: 'error', message: 'Failed to set current version' }));
            return rejectWithValue(err.response?.data?.message || 'Failed to set current version');
        }
    }
);

export const deleteModelVersion = createAsyncThunk(
    'projectModels/deleteVersion',
    async ({ projectId, modelId, versionId }, { dispatch, rejectWithValue }) => {
        try {
            await modelAPI.delete(
                `/v0.4/projects/${projectId}/models/${modelId}/versions/${versionId}`
            );
            dispatch(setSnackbar({ status: 'success', message: 'Version deleted' }));
            dispatch(fetchProjectModels({ projectId }));
            return { modelId, versionId };
        } catch (err) {
            const message =
                err.response?.status === 409
                    ? 'Cannot delete the last version of a model'
                    : 'Failed to delete version';
            dispatch(setSnackbar({ status: 'error', message }));
            return rejectWithValue(err.response?.data?.message || message);
        }
    }
);

const projectModelSlice = createSlice({
    name: 'projectModels',
    initialState: {
        models: [],
        // keyed by modelId → { version: number, files: [] }
        filesByModelId: {},
        loading: false,
        filesLoading: false,
        mutating: false,
        error: null,
    },
    reducers: {
        clearModelError(state) {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProjectModels.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProjectModels.fulfilled, (state, action) => {
                state.loading = false;
                state.models = action.payload.project?.models ?? [];
            })
            .addCase(fetchProjectModels.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchModelFiles.pending, (state) => {
                state.filesLoading = true;
            })
            .addCase(fetchModelFiles.fulfilled, (state, action) => {
                state.filesLoading = false;
                state.filesByModelId[action.payload.modelId] = {
                    version: action.payload.version,
                    files: action.payload.files,
                };
            })
            .addCase(fetchModelFiles.rejected, (state, action) => {
                state.filesLoading = false;
                state.error = action.payload;
            });

        // Mutations (update/delete model + version, set current) share a flag
        const mutationThunks = [
            updateProjectModel,
            deleteProjectModel,
            updateModelVersion,
            setCurrentModelVersion,
            deleteModelVersion,
        ];
        mutationThunks.forEach((thunk) => {
            builder
                .addCase(thunk.pending, (state) => {
                    state.mutating = true;
                    state.error = null;
                })
                .addCase(thunk.fulfilled, (state) => {
                    state.mutating = false;
                })
                .addCase(thunk.rejected, (state, action) => {
                    state.mutating = false;
                    state.error = action.payload;
                });
        });
    },
});

export const { clearModelError } = projectModelSlice.actions;
export default projectModelSlice.reducer;
